// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ════════════════════════════════════════════════════════════════════════
   Tables, filters and dashboards

   The three places the desktop app leaks hardest onto a phone:

     1. Wide tables (8 columns of seller products, admin orders, payouts)
     2. Grids of KPI cards laid out for a 1400px canvas
     3. Toolbars that assume a search box, three selects and a button fit
        on one line

   None of these are shrunk here. Tables become stacked cards, KPI grids
   become a swipeable metric rail, toolbars become a sticky search plus a
   filter sheet.
   ════════════════════════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════════════════════════
   Tables → cards

   Each <tr> becomes a card and each <td> a labelled line inside it, using
   the \`data-label\` attribute added to the cells in the portal components.
   The <thead> is visually hidden rather than removed, so the header text
   stays available to assistive technology and the table keeps its semantics.

   Cells opt out of the label treatment with data-label="" (the leading
   identity cell, which becomes the card's title, and the trailing actions
   cell, which becomes its footer).
   ════════════════════════════════════════════════════════════════════════ */

html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) {
  display: block;
  width: 100%;
  border: none;
  border-spacing: 0;
  background: transparent;
}

html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) thead {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) tbody {
  display: flex;
  flex-direction: column;
  gap: var(--m-3);
  width: 100%;
}

html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) tr {
  display: block;
  width: 100%;
  padding: var(--m-3) var(--m-4);
  border: 1px solid var(--m-hairline);
  border-radius: var(--m-r);
  background: var(--m-surface-card);
  box-shadow: var(--m-shadow-sm);
}

html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--m-3);
  width: 100%;
  min-height: 32px;
  padding: var(--m-1) 0;
  border: none;
  font-size: var(--m-fs-secondary);
  text-align: right;
}

/* The label, drawn from the column header. */
html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[data-label]:not([data-label=""])::before {
  content: attr(data-label);
  flex: 0 0 auto;
  font-size: var(--m-fs-label);
  font-weight: var(--m-fw-medium);
  color: var(--color-text-secondary);
  text-align: left;
  white-space: nowrap;
}

/* Identity cell → the card's title row: full width, left aligned, no label,
   separated from the fields beneath it. */
html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[data-label=""]:first-child {
  display: block;
  text-align: left;
  padding-bottom: var(--m-3);
  margin-bottom: var(--m-2);
  border-bottom: 1px solid var(--m-hairline);
  font-size: var(--m-fs-body);
}

/* Actions cell → the card's footer. */
html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[data-label=""]:last-child {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: var(--m-2);
  padding-top: var(--m-3);
  margin-top: var(--m-2);
  border-top: 1px solid var(--m-hairline);
}

html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[data-label=""]:last-child button {
  flex: 1 1 auto;
  min-height: var(--m-touch);
  padding: 0 var(--m-3);
  border-radius: var(--m-r-xs);
  font-size: var(--m-fs-secondary);
  white-space: nowrap;
}

/* An empty-state row spans the card rather than pretending to be a field. */
html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[colspan] {
  display: block;
  text-align: center;
  padding: var(--m-6) var(--m-3);
  color: var(--color-text-secondary);
}

html.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[colspan]::before {
  content: none;
}

/* Product/identity cell internals */
html.m-app .sp-product-cell,
html.m-app .sl-seller-cell {
  display: flex;
  align-items: center;
  gap: var(--m-3);
  min-width: 0;
}

html.m-app .sp-product-img,
html.m-app .sl-seller-avatar {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: var(--m-r-xs);
  overflow: hidden;
  flex: 0 0 auto;
}

html.m-app .sp-product-img img {
  width: 100%; height: 100%; object-fit: cover;
}

html.m-app .sp-product-name {
  font-size: var(--m-fs-body);
  font-weight: var(--m-fw-semibold);
  line-height: var(--m-lh-snug);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

html.m-app .sp-product-sku {
  font-size: var(--m-fs-caption);
  color: var(--color-text-muted);
  margin: 2px 0 0;
}

/* Any table left in a horizontal scroller (a genuinely numeric grid where
   stacking would lose the comparison) at least scrolls inside its own box
   rather than pushing the page sideways. */
html.m-app .table-scroll,
html.m-app .m-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-inline: calc(var(--m-gutter) * -1);
  padding-inline: var(--m-gutter);
}

/* ════════════════════════════════════════════════════════════════════════
   Metric rails

   Nine status cards in a 2-column grid is three screens of scrolling before
   the first order — which is what the order history page did. The same nine
   become one horizontally-swipeable rail of compact metrics that doubles as
   the status filter, so the summary costs ~90px instead of ~900px.
   ════════════════════════════════════════════════════════════════════════ */

html.m-app :is(.oh-stats-grid, .dash-stats-grid, .sp-stats-grid, .kpi-grid, .m-metrics) {
  display: flex;
  flex-wrap: nowrap;
  gap: var(--m-2);
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  margin: 0 calc(var(--m-gutter) * -1) var(--m-4);
  padding: var(--m-1) var(--m-gutter);
}

html.m-app :is(.oh-stats-grid, .dash-stats-grid, .sp-stats-grid, .kpi-grid, .m-metrics)::-webkit-scrollbar {
  display: none;
}

html.m-app :is(.oh-stat-card, .dash-stat-card, .sp-stat-card, .kpi-card, .m-metric) {
  flex: 0 0 auto;
  /* Sized so a second card is half-visible at the right edge — the peek
     that says the rail scrolls. */
  width: 132px;
  min-height: 76px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 2px;
  padding: var(--m-3);
  margin: 0;
  border: 1px solid var(--m-hairline);
  border-radius: var(--m-r-sm);
  background: var(--m-surface-card);
  box-shadow: var(--m-shadow-sm);
  scroll-snap-align: start;
  text-align: left;
}

html.m-app :is(.oh-stat-card, .dash-stat-card, .kpi-card).active,
html.m-app :is(.oh-stat-card, .dash-stat-card, .kpi-card)[aria-pressed="true"] {
  border-color: var(--color-secondary);
  background: var(--color-secondary-soft);
}

html.m-app :is(.oh-stat-icon, .dash-stat-icon, .kpi-icon) {
  width: var(--m-icon);
  height: var(--m-icon);
  min-width: var(--m-icon);
  border-radius: var(--m-r-xs);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--m-1);
  background: transparent !important;  /* inline iconBg + '22' from JS */
}

html.m-app :is(.oh-stat-icon, .dash-stat-icon, .kpi-icon) svg {
  width: var(--m-icon-sm); height: var(--m-icon-sm);
}

html.m-app :is(.oh-stat-label, .dash-stat-label, .kpi-label) {
  order: -1;
  font-size: var(--m-fs-caption);
  font-weight: var(--m-fw-medium);
  color: var(--color-text-secondary);
  line-height: 1.2;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

html.m-app :is(.oh-stat-count, .dash-stat-value, .kpi-value) {
  font-size: 1.25rem;      /* 20px — the number is the point of the card */
  font-weight: var(--m-fw-bold);
  line-height: 1.15;
  letter-spacing: var(--m-track-title);
  font-variant-numeric: tabular-nums;
  margin: 0;
}

html.m-app :is(.oh-stat-sub, .dash-stat-sub, .kpi-sub) {
  font-size: var(--m-fs-caption);
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
  margin: 0;
  white-space: nowrap;
}

html.m-app .oh-stat-body {
  display: contents;
}

/* ════════════════════════════════════════════════════════════════════════
   Search and filter toolbars
   ════════════════════════════════════════════════════════════════════════ */

html.m-app :is(.oh-filters-bar, .filter-bar, .sp-toolbar, .sl-toolbar, .m-toolbar) {
  display: flex;
  align-items: center;
  gap: var(--m-2);
  padding: 0 var(--m-gutter) var(--m-3);
  flex-wrap: nowrap;
}

/* The border, background and radius belong to the WRAPPER, which is what
   already holds the search icon; the input inside stays transparent and
   borderless. Styling both produced a pill floating inside a rounded
   rectangle on the orders screen. */
html.m-app :is(.oh-search-wrap, .filter-search-wrap, .search-box, .m-search) {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: var(--m-2);
  height: var(--m-touch);
  min-height: var(--m-touch);
  padding: 0 var(--m-3);
  border: 1px solid var(--m-hairline);
  border-radius: var(--m-r-pill);
  background: var(--m-surface-sunken);
}

html.m-app :is(.oh-search-wrap, .filter-search-wrap, .search-box, .m-search):focus-within {
  border-color: var(--color-secondary);
  background: var(--m-surface-card);
}

html.m-app :is(.oh-search-input, .filter-search-input, .search-input, .m-search input) {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  height: 100%;
  min-height: 0;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  /* 16px keeps iOS from zooming the viewport on focus. */
  font-size: 1rem;
}

html.m-app :is(.oh-search-input, .filter-search-input, .search-input)::placeholder {
  font-size: var(--m-fs-body);
  color: var(--color-text-muted);
}

html.m-app :is(.oh-search-icon, .filter-search-icon) {
  position: static;
  flex: 0 0 auto;
  display: inline-flex;
  color: var(--color-text-muted);
  pointer-events: none;
}

/* Secondary filter controls collapse behind one button that opens a sheet,
   rather than stacking three full-width selects above the results. */
html.m-app .m-filter-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--m-1);
  height: var(--m-touch);
  min-height: var(--m-touch);
  padding: 0 var(--m-3);
  border: 1px solid var(--m-hairline-strong);
  border-radius: var(--m-r-pill);
  background: var(--m-surface-card);
  color: var(--color-text-primary);
  font-size: var(--m-fs-secondary);
  font-weight: var(--m-fw-medium);
}

html.m-app .m-filter-btn[data-count]:not([data-count="0"])::after {
  content: attr(data-count);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: var(--m-r-pill);
  background: var(--color-secondary);
  color: var(--color-text-on-secondary);
  font-size: var(--m-fs-caption);
  font-weight: var(--m-fw-bold);
}

/* A horizontal chip rail is the mobile-native way to expose the two or
   three filters that matter most without opening anything. */
html.m-app .m-chip-rail {
  display: flex;
  gap: var(--m-2);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  padding: 0 var(--m-gutter) var(--m-3);
}

html.m-app .m-chip-rail::-webkit-scrollbar { display: none; }

/* ════════════════════════════════════════════════════════════════════════
   Charts

   recharts writes --chart-* into SVG attributes, so the series colours are
   inherited untouched. What changes is the frame: shorter, fewer ticks, and
   a legend that wraps under rather than stealing horizontal space.
   ════════════════════════════════════════════════════════════════════════ */

html.m-app .recharts-responsive-container {
  /* A 300px-tall chart on an 844px screen is a third of the viewport. */
  max-height: 200px;
}

html.m-app .recharts-cartesian-axis-tick text,
html.m-app .recharts-legend-item-text {
  font-size: var(--m-fs-caption);
}

html.m-app .recharts-legend-wrapper {
  position: static !important;
  display: flex;
  flex-wrap: wrap;
  gap: var(--m-1) var(--m-3);
  padding-top: var(--m-2);
}

html.m-app .recharts-tooltip-wrapper .recharts-default-tooltip {
  border-radius: var(--m-r-xs) !important;
  border: 1px solid var(--m-hairline) !important;
  font-size: var(--m-fs-secondary) !important;
  padding: var(--m-2) var(--m-3) !important;
}

/* Charts that only make sense wide scroll inside their own box. */
html.m-app .m-chart-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

html.m-app .m-chart-scroll > * {
  min-width: 480px;
}

/* ════════════════════════════════════════════════════════════════════════
   Portal shells

   Seller and admin portals are built around a persistent left sidebar. A
   sidebar is not a mobile navigation pattern — it either eats half the
   width or hides behind a hamburger nobody presses. Both portals already
   ship a mobile drawer; this makes sure the sidebar itself is out of flow
   and the content takes the full screen.
   ════════════════════════════════════════════════════════════════════════ */

html.m-app :is(.sp-layout, .admin-layout, .sa-layout) {
  display: block;
  grid-template-columns: none;
}

html.m-app :is(.sp-main, .admin-main, .sa-main) {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: var(--m-4) 0 calc(var(--m-bottomnav-total) + var(--m-6));
}

/* Sidebar → off-canvas drawer. See portals.css for the seller portal's
   own collapse mechanism, which uses .sp-sidebar-collapsed rather than an
   .open class and therefore needs the inverse rule. */
html.m-app :is(.admin-sidebar, .sa-sidebar):not(.open) {
  display: none;
}

html.m-app :is(.admin-sidebar, .sa-sidebar).open {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 210;
  width: min(84vw, 320px);
  padding-top: var(--m-safe-top);
  padding-bottom: var(--m-safe-bottom);
  overflow-y: auto;
  box-shadow: var(--m-shadow-raised);
}
`, "",{"version":3,"sources":["webpack://./src/mobile/data.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;6EAa6E;;AAE7E;;;;;;;;;;;6EAW6E;;AAE7E;EACE,cAAc;EACd,WAAW;EACX,YAAY;EACZ,iBAAiB;EACjB,uBAAuB;AACzB;;AAEA;EACE,kBAAkB;EAClB,UAAU;EACV,WAAW;EACX,gBAAgB;EAChB,mBAAmB;EACnB,qBAAqB;EACrB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,eAAe;EACf,WAAW;AACb;;AAEA;EACE,cAAc;EACd,WAAW;EACX,8BAA8B;EAC9B,mCAAmC;EACnC,yBAAyB;EACzB,iCAAiC;EACjC,8BAA8B;AAChC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,eAAe;EACf,WAAW;EACX,gBAAgB;EAChB,qBAAqB;EACrB,YAAY;EACZ,gCAAgC;EAChC,iBAAiB;AACnB;;AAEA,6CAA6C;AAC7C;EACE,yBAAyB;EACzB,cAAc;EACd,4BAA4B;EAC5B,+BAA+B;EAC/B,kCAAkC;EAClC,gBAAgB;EAChB,mBAAmB;AACrB;;AAEA;0CAC0C;AAC1C;EACE,cAAc;EACd,gBAAgB;EAChB,0BAA0B;EAC1B,yBAAyB;EACzB,0CAA0C;EAC1C,2BAA2B;AAC7B;;AAEA,sCAAsC;AACtC;EACE,aAAa;EACb,eAAe;EACf,2BAA2B;EAC3B,eAAe;EACf,uBAAuB;EACvB,sBAAsB;EACtB,uCAAuC;AACzC;;AAEA;EACE,cAAc;EACd,0BAA0B;EAC1B,qBAAqB;EACrB,4BAA4B;EAC5B,gCAAgC;EAChC,mBAAmB;AACrB;;AAEA,4EAA4E;AAC5E;EACE,cAAc;EACd,kBAAkB;EAClB,8BAA8B;EAC9B,kCAAkC;AACpC;;AAEA;EACE,aAAa;AACf;;AAEA,oCAAoC;AACpC;;EAEE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,YAAY;AACd;;AAEA;;EAEE,WAAW;EACX,YAAY;EACZ,eAAe;EACf,4BAA4B;EAC5B,gBAAgB;EAChB,cAAc;AAChB;;AAEA;EACE,WAAW,EAAE,YAAY,EAAE,iBAAiB;AAC9C;;AAEA;EACE,2BAA2B;EAC3B,iCAAiC;EACjC,6BAA6B;EAC7B,SAAS;EACT,gBAAgB;EAChB,uBAAuB;EACvB,oBAAoB;EACpB,qBAAqB;EACrB,4BAA4B;AAC9B;;AAEA;EACE,8BAA8B;EAC9B,8BAA8B;EAC9B,eAAe;AACjB;;AAEA;;2CAE2C;AAC3C;;EAEE,gBAAgB;EAChB,iCAAiC;EACjC,yCAAyC;EACzC,+BAA+B;AACjC;;AAEA;;;;;;;6EAO6E;;AAE7E;EACE,aAAa;EACb,iBAAiB;EACjB,eAAe;EACf,gBAAgB;EAChB,kBAAkB;EAClB,iCAAiC;EACjC,6BAA6B;EAC7B,qBAAqB;EACrB,+CAA+C;EAC/C,mCAAmC;AACrC;;AAEA;EACE,aAAa;AACf;;AAEA;EACE,cAAc;EACd;kCACgC;EAChC,YAAY;EACZ,gBAAgB;EAChB,aAAa;EACb,sBAAsB;EACtB,uBAAuB;EACvB,uBAAuB;EACvB,QAAQ;EACR,mBAAmB;EACnB,SAAS;EACT,mCAAmC;EACnC,4BAA4B;EAC5B,iCAAiC;EACjC,8BAA8B;EAC9B,wBAAwB;EACxB,gBAAgB;AAClB;;AAEA;;EAEE,oCAAoC;EACpC,uCAAuC;AACzC;;AAEA;EACE,oBAAoB;EACpB,qBAAqB;EACrB,wBAAwB;EACxB,4BAA4B;EAC5B,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,yBAAyB;EACzB,kCAAkC,GAAG,iCAAiC;AACxE;;AAEA;EACE,uBAAuB,EAAE,wBAAwB;AACnD;;AAEA;EACE,SAAS;EACT,8BAA8B;EAC9B,+BAA+B;EAC/B,kCAAkC;EAClC,gBAAgB;EAChB,SAAS;EACT,mBAAmB;EACnB,gBAAgB;EAChB,uBAAuB;EACvB,eAAe;AACjB;;AAEA;EACE,kBAAkB,OAAO,+CAA+C;EACxE,6BAA6B;EAC7B,iBAAiB;EACjB,oCAAoC;EACpC,kCAAkC;EAClC,SAAS;AACX;;AAEA;EACE,8BAA8B;EAC9B,8BAA8B;EAC9B,kCAAkC;EAClC,SAAS;EACT,mBAAmB;AACrB;;AAEA;EACE,iBAAiB;AACnB;;AAEA;;6EAE6E;;AAE7E;EACE,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,qCAAqC;EACrC,iBAAiB;AACnB;;AAEA;;;oCAGoC;AACpC;EACE,kBAAkB;EAClB,cAAc;EACd,YAAY;EACZ,aAAa;EACb,mBAAmB;EACnB,eAAe;EACf,sBAAsB;EACtB,0BAA0B;EAC1B,qBAAqB;EACrB,mCAAmC;EACnC,8BAA8B;EAC9B,mCAAmC;AACrC;;AAEA;EACE,oCAAoC;EACpC,iCAAiC;AACnC;;AAEA;EACE,cAAc;EACd,WAAW;EACX,YAAY;EACZ,YAAY;EACZ,aAAa;EACb,UAAU;EACV,YAAY;EACZ,gBAAgB;EAChB,uBAAuB;EACvB,uDAAuD;EACvD,eAAe;AACjB;;AAEA;EACE,2BAA2B;EAC3B,8BAA8B;AAChC;;AAEA;EACE,gBAAgB;EAChB,cAAc;EACd,oBAAoB;EACpB,8BAA8B;EAC9B,oBAAoB;AACtB;;AAEA;qEACqE;AACrE;EACE,cAAc;EACd,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,sBAAsB;EACtB,0BAA0B;EAC1B,qBAAqB;EACrB,0CAA0C;EAC1C,8BAA8B;EAC9B,iCAAiC;EACjC,gCAAgC;EAChC,gCAAgC;EAChC,+BAA+B;AACjC;;AAEA;EACE,yBAAyB;EACzB,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,YAAY;EACZ,cAAc;EACd,8BAA8B;EAC9B,kCAAkC;EAClC,qCAAqC;EACrC,8BAA8B;EAC9B,6BAA6B;AAC/B;;AAEA;6DAC6D;AAC7D;EACE,aAAa;EACb,eAAe;EACf,gBAAgB;EAChB,iCAAiC;EACjC,6BAA6B;EAC7B,qBAAqB;EACrB,qCAAqC;AACvC;;AAEA,6CAA6C,aAAa,EAAE;;AAE5D;;;;;;6EAM6E;;AAE7E;EACE,sEAAsE;EACtE,iBAAiB;AACnB;;AAEA;;EAEE,8BAA8B;AAChC;;AAEA;EACE,2BAA2B;EAC3B,aAAa;EACb,eAAe;EACf,0BAA0B;EAC1B,uBAAuB;AACzB;;AAEA;EACE,uCAAuC;EACvC,8CAA8C;EAC9C,2CAA2C;EAC3C,yCAAyC;AAC3C;;AAEA,kEAAkE;AAClE;EACE,gBAAgB;EAChB,iCAAiC;AACnC;;AAEA;EACE,gBAAgB;AAClB;;AAEA;;;;;;;;6EAQ6E;;AAE7E;EACE,cAAc;EACd,2BAA2B;AAC7B;;AAEA;EACE,WAAW;EACX,eAAe;EACf,SAAS;EACT,iEAAiE;AACnE;;AAEA;;sDAEsD;AACtD;EACE,aAAa;AACf;;AAEA;EACE,eAAe;EACf,iBAAiB;EACjB,YAAY;EACZ,uBAAuB;EACvB,8BAA8B;EAC9B,oCAAoC;EACpC,gBAAgB;EAChB,kCAAkC;AACpC","sourcesContent":["/* ════════════════════════════════════════════════════════════════════════\n   Tables, filters and dashboards\n\n   The three places the desktop app leaks hardest onto a phone:\n\n     1. Wide tables (8 columns of seller products, admin orders, payouts)\n     2. Grids of KPI cards laid out for a 1400px canvas\n     3. Toolbars that assume a search box, three selects and a button fit\n        on one line\n\n   None of these are shrunk here. Tables become stacked cards, KPI grids\n   become a swipeable metric rail, toolbars become a sticky search plus a\n   filter sheet.\n   ════════════════════════════════════════════════════════════════════════ */\n\n/* ════════════════════════════════════════════════════════════════════════\n   Tables → cards\n\n   Each <tr> becomes a card and each <td> a labelled line inside it, using\n   the `data-label` attribute added to the cells in the portal components.\n   The <thead> is visually hidden rather than removed, so the header text\n   stays available to assistive technology and the table keeps its semantics.\n\n   Cells opt out of the label treatment with data-label=\"\" (the leading\n   identity cell, which becomes the card's title, and the trailing actions\n   cell, which becomes its footer).\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) {\n  display: block;\n  width: 100%;\n  border: none;\n  border-spacing: 0;\n  background: transparent;\n}\n\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) thead {\n  position: absolute;\n  width: 1px;\n  height: 1px;\n  overflow: hidden;\n  clip: rect(0 0 0 0);\n  clip-path: inset(50%);\n  white-space: nowrap;\n}\n\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) tbody {\n  display: flex;\n  flex-direction: column;\n  gap: var(--m-3);\n  width: 100%;\n}\n\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) tr {\n  display: block;\n  width: 100%;\n  padding: var(--m-3) var(--m-4);\n  border: 1px solid var(--m-hairline);\n  border-radius: var(--m-r);\n  background: var(--m-surface-card);\n  box-shadow: var(--m-shadow-sm);\n}\n\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: var(--m-3);\n  width: 100%;\n  min-height: 32px;\n  padding: var(--m-1) 0;\n  border: none;\n  font-size: var(--m-fs-secondary);\n  text-align: right;\n}\n\n/* The label, drawn from the column header. */\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[data-label]:not([data-label=\"\"])::before {\n  content: attr(data-label);\n  flex: 0 0 auto;\n  font-size: var(--m-fs-label);\n  font-weight: var(--m-fw-medium);\n  color: var(--color-text-secondary);\n  text-align: left;\n  white-space: nowrap;\n}\n\n/* Identity cell → the card's title row: full width, left aligned, no label,\n   separated from the fields beneath it. */\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[data-label=\"\"]:first-child {\n  display: block;\n  text-align: left;\n  padding-bottom: var(--m-3);\n  margin-bottom: var(--m-2);\n  border-bottom: 1px solid var(--m-hairline);\n  font-size: var(--m-fs-body);\n}\n\n/* Actions cell → the card's footer. */\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[data-label=\"\"]:last-child {\n  display: flex;\n  flex-wrap: wrap;\n  justify-content: flex-start;\n  gap: var(--m-2);\n  padding-top: var(--m-3);\n  margin-top: var(--m-2);\n  border-top: 1px solid var(--m-hairline);\n}\n\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[data-label=\"\"]:last-child button {\n  flex: 1 1 auto;\n  min-height: var(--m-touch);\n  padding: 0 var(--m-3);\n  border-radius: var(--m-r-xs);\n  font-size: var(--m-fs-secondary);\n  white-space: nowrap;\n}\n\n/* An empty-state row spans the card rather than pretending to be a field. */\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[colspan] {\n  display: block;\n  text-align: center;\n  padding: var(--m-6) var(--m-3);\n  color: var(--color-text-secondary);\n}\n\nhtml.m-app :is(.sp-table, .sl-table, .ro-table, .admin-table, .dash-orders-table, .m-table) td[colspan]::before {\n  content: none;\n}\n\n/* Product/identity cell internals */\nhtml.m-app .sp-product-cell,\nhtml.m-app .sl-seller-cell {\n  display: flex;\n  align-items: center;\n  gap: var(--m-3);\n  min-width: 0;\n}\n\nhtml.m-app .sp-product-img,\nhtml.m-app .sl-seller-avatar {\n  width: 40px;\n  height: 40px;\n  min-width: 40px;\n  border-radius: var(--m-r-xs);\n  overflow: hidden;\n  flex: 0 0 auto;\n}\n\nhtml.m-app .sp-product-img img {\n  width: 100%; height: 100%; object-fit: cover;\n}\n\nhtml.m-app .sp-product-name {\n  font-size: var(--m-fs-body);\n  font-weight: var(--m-fw-semibold);\n  line-height: var(--m-lh-snug);\n  margin: 0;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  display: -webkit-box;\n  -webkit-line-clamp: 2;\n  -webkit-box-orient: vertical;\n}\n\nhtml.m-app .sp-product-sku {\n  font-size: var(--m-fs-caption);\n  color: var(--color-text-muted);\n  margin: 2px 0 0;\n}\n\n/* Any table left in a horizontal scroller (a genuinely numeric grid where\n   stacking would lose the comparison) at least scrolls inside its own box\n   rather than pushing the page sideways. */\nhtml.m-app .table-scroll,\nhtml.m-app .m-table-scroll {\n  overflow-x: auto;\n  -webkit-overflow-scrolling: touch;\n  margin-inline: calc(var(--m-gutter) * -1);\n  padding-inline: var(--m-gutter);\n}\n\n/* ════════════════════════════════════════════════════════════════════════\n   Metric rails\n\n   Nine status cards in a 2-column grid is three screens of scrolling before\n   the first order — which is what the order history page did. The same nine\n   become one horizontally-swipeable rail of compact metrics that doubles as\n   the status filter, so the summary costs ~90px instead of ~900px.\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app :is(.oh-stats-grid, .dash-stats-grid, .sp-stats-grid, .kpi-grid, .m-metrics) {\n  display: flex;\n  flex-wrap: nowrap;\n  gap: var(--m-2);\n  overflow-x: auto;\n  overflow-y: hidden;\n  -webkit-overflow-scrolling: touch;\n  scroll-snap-type: x proximity;\n  scrollbar-width: none;\n  margin: 0 calc(var(--m-gutter) * -1) var(--m-4);\n  padding: var(--m-1) var(--m-gutter);\n}\n\nhtml.m-app :is(.oh-stats-grid, .dash-stats-grid, .sp-stats-grid, .kpi-grid, .m-metrics)::-webkit-scrollbar {\n  display: none;\n}\n\nhtml.m-app :is(.oh-stat-card, .dash-stat-card, .sp-stat-card, .kpi-card, .m-metric) {\n  flex: 0 0 auto;\n  /* Sized so a second card is half-visible at the right edge — the peek\n     that says the rail scrolls. */\n  width: 132px;\n  min-height: 76px;\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  justify-content: center;\n  gap: 2px;\n  padding: var(--m-3);\n  margin: 0;\n  border: 1px solid var(--m-hairline);\n  border-radius: var(--m-r-sm);\n  background: var(--m-surface-card);\n  box-shadow: var(--m-shadow-sm);\n  scroll-snap-align: start;\n  text-align: left;\n}\n\nhtml.m-app :is(.oh-stat-card, .dash-stat-card, .kpi-card).active,\nhtml.m-app :is(.oh-stat-card, .dash-stat-card, .kpi-card)[aria-pressed=\"true\"] {\n  border-color: var(--color-secondary);\n  background: var(--color-secondary-soft);\n}\n\nhtml.m-app :is(.oh-stat-icon, .dash-stat-icon, .kpi-icon) {\n  width: var(--m-icon);\n  height: var(--m-icon);\n  min-width: var(--m-icon);\n  border-radius: var(--m-r-xs);\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  margin-bottom: var(--m-1);\n  background: transparent !important;  /* inline iconBg + '22' from JS */\n}\n\nhtml.m-app :is(.oh-stat-icon, .dash-stat-icon, .kpi-icon) svg {\n  width: var(--m-icon-sm); height: var(--m-icon-sm);\n}\n\nhtml.m-app :is(.oh-stat-label, .dash-stat-label, .kpi-label) {\n  order: -1;\n  font-size: var(--m-fs-caption);\n  font-weight: var(--m-fw-medium);\n  color: var(--color-text-secondary);\n  line-height: 1.2;\n  margin: 0;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  max-width: 100%;\n}\n\nhtml.m-app :is(.oh-stat-count, .dash-stat-value, .kpi-value) {\n  font-size: 1.25rem;      /* 20px — the number is the point of the card */\n  font-weight: var(--m-fw-bold);\n  line-height: 1.15;\n  letter-spacing: var(--m-track-title);\n  font-variant-numeric: tabular-nums;\n  margin: 0;\n}\n\nhtml.m-app :is(.oh-stat-sub, .dash-stat-sub, .kpi-sub) {\n  font-size: var(--m-fs-caption);\n  color: var(--color-text-muted);\n  font-variant-numeric: tabular-nums;\n  margin: 0;\n  white-space: nowrap;\n}\n\nhtml.m-app .oh-stat-body {\n  display: contents;\n}\n\n/* ════════════════════════════════════════════════════════════════════════\n   Search and filter toolbars\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app :is(.oh-filters-bar, .filter-bar, .sp-toolbar, .sl-toolbar, .m-toolbar) {\n  display: flex;\n  align-items: center;\n  gap: var(--m-2);\n  padding: 0 var(--m-gutter) var(--m-3);\n  flex-wrap: nowrap;\n}\n\n/* The border, background and radius belong to the WRAPPER, which is what\n   already holds the search icon; the input inside stays transparent and\n   borderless. Styling both produced a pill floating inside a rounded\n   rectangle on the orders screen. */\nhtml.m-app :is(.oh-search-wrap, .filter-search-wrap, .search-box, .m-search) {\n  position: relative;\n  flex: 1 1 auto;\n  min-width: 0;\n  display: flex;\n  align-items: center;\n  gap: var(--m-2);\n  height: var(--m-touch);\n  min-height: var(--m-touch);\n  padding: 0 var(--m-3);\n  border: 1px solid var(--m-hairline);\n  border-radius: var(--m-r-pill);\n  background: var(--m-surface-sunken);\n}\n\nhtml.m-app :is(.oh-search-wrap, .filter-search-wrap, .search-box, .m-search):focus-within {\n  border-color: var(--color-secondary);\n  background: var(--m-surface-card);\n}\n\nhtml.m-app :is(.oh-search-input, .filter-search-input, .search-input, .m-search input) {\n  flex: 1 1 auto;\n  width: 100%;\n  min-width: 0;\n  height: 100%;\n  min-height: 0;\n  padding: 0;\n  border: none;\n  border-radius: 0;\n  background: transparent;\n  /* 16px keeps iOS from zooming the viewport on focus. */\n  font-size: 1rem;\n}\n\nhtml.m-app :is(.oh-search-input, .filter-search-input, .search-input)::placeholder {\n  font-size: var(--m-fs-body);\n  color: var(--color-text-muted);\n}\n\nhtml.m-app :is(.oh-search-icon, .filter-search-icon) {\n  position: static;\n  flex: 0 0 auto;\n  display: inline-flex;\n  color: var(--color-text-muted);\n  pointer-events: none;\n}\n\n/* Secondary filter controls collapse behind one button that opens a sheet,\n   rather than stacking three full-width selects above the results. */\nhtml.m-app .m-filter-btn {\n  flex: 0 0 auto;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: var(--m-1);\n  height: var(--m-touch);\n  min-height: var(--m-touch);\n  padding: 0 var(--m-3);\n  border: 1px solid var(--m-hairline-strong);\n  border-radius: var(--m-r-pill);\n  background: var(--m-surface-card);\n  color: var(--color-text-primary);\n  font-size: var(--m-fs-secondary);\n  font-weight: var(--m-fw-medium);\n}\n\nhtml.m-app .m-filter-btn[data-count]:not([data-count=\"0\"])::after {\n  content: attr(data-count);\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  min-width: 18px;\n  height: 18px;\n  padding: 0 5px;\n  border-radius: var(--m-r-pill);\n  background: var(--color-secondary);\n  color: var(--color-text-on-secondary);\n  font-size: var(--m-fs-caption);\n  font-weight: var(--m-fw-bold);\n}\n\n/* A horizontal chip rail is the mobile-native way to expose the two or\n   three filters that matter most without opening anything. */\nhtml.m-app .m-chip-rail {\n  display: flex;\n  gap: var(--m-2);\n  overflow-x: auto;\n  -webkit-overflow-scrolling: touch;\n  scroll-snap-type: x proximity;\n  scrollbar-width: none;\n  padding: 0 var(--m-gutter) var(--m-3);\n}\n\nhtml.m-app .m-chip-rail::-webkit-scrollbar { display: none; }\n\n/* ════════════════════════════════════════════════════════════════════════\n   Charts\n\n   recharts writes --chart-* into SVG attributes, so the series colours are\n   inherited untouched. What changes is the frame: shorter, fewer ticks, and\n   a legend that wraps under rather than stealing horizontal space.\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app .recharts-responsive-container {\n  /* A 300px-tall chart on an 844px screen is a third of the viewport. */\n  max-height: 200px;\n}\n\nhtml.m-app .recharts-cartesian-axis-tick text,\nhtml.m-app .recharts-legend-item-text {\n  font-size: var(--m-fs-caption);\n}\n\nhtml.m-app .recharts-legend-wrapper {\n  position: static !important;\n  display: flex;\n  flex-wrap: wrap;\n  gap: var(--m-1) var(--m-3);\n  padding-top: var(--m-2);\n}\n\nhtml.m-app .recharts-tooltip-wrapper .recharts-default-tooltip {\n  border-radius: var(--m-r-xs) !important;\n  border: 1px solid var(--m-hairline) !important;\n  font-size: var(--m-fs-secondary) !important;\n  padding: var(--m-2) var(--m-3) !important;\n}\n\n/* Charts that only make sense wide scroll inside their own box. */\nhtml.m-app .m-chart-scroll {\n  overflow-x: auto;\n  -webkit-overflow-scrolling: touch;\n}\n\nhtml.m-app .m-chart-scroll > * {\n  min-width: 480px;\n}\n\n/* ════════════════════════════════════════════════════════════════════════\n   Portal shells\n\n   Seller and admin portals are built around a persistent left sidebar. A\n   sidebar is not a mobile navigation pattern — it either eats half the\n   width or hides behind a hamburger nobody presses. Both portals already\n   ship a mobile drawer; this makes sure the sidebar itself is out of flow\n   and the content takes the full screen.\n   ════════════════════════════════════════════════════════════════════════ */\n\nhtml.m-app :is(.sp-layout, .admin-layout, .sa-layout) {\n  display: block;\n  grid-template-columns: none;\n}\n\nhtml.m-app :is(.sp-main, .admin-main, .sa-main) {\n  width: 100%;\n  max-width: none;\n  margin: 0;\n  padding: var(--m-4) 0 calc(var(--m-bottomnav-total) + var(--m-6));\n}\n\n/* Sidebar → off-canvas drawer. See portals.css for the seller portal's\n   own collapse mechanism, which uses .sp-sidebar-collapsed rather than an\n   .open class and therefore needs the inverse rule. */\nhtml.m-app :is(.admin-sidebar, .sa-sidebar):not(.open) {\n  display: none;\n}\n\nhtml.m-app :is(.admin-sidebar, .sa-sidebar).open {\n  position: fixed;\n  inset: 0 auto 0 0;\n  z-index: 210;\n  width: min(84vw, 320px);\n  padding-top: var(--m-safe-top);\n  padding-bottom: var(--m-safe-bottom);\n  overflow-y: auto;\n  box-shadow: var(--m-shadow-raised);\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
